import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Search, Edit2, Trash2, Mail, Loader2, Users, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", mobile: "", address: "", child_names: "", status: true, send_welcome: true };

export default function AdminParents() {
    const [parents, setParents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [child, setChild] = useState("");
    const [openForm, setOpenForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (search) params.search = search;
        if (child) params.child = child;
        const r = await api.get("/admin/parents", { params });
        setParents(r.data.parents);
        setLoading(false);
    }, [search, child]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setEditing(null);
        setForm(empty);
        setOpenForm(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({
            name: p.name,
            email: p.email,
            mobile: p.mobile || "",
            address: p.address || "",
            child_names: (p.child_names || []).join(", "),
            status: p.status,
            send_welcome: false,
        });
        setOpenForm(true);
    };

    const save = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                name: form.name,
                email: form.email,
                mobile: form.mobile,
                address: form.address,
                child_names: form.child_names.split(",").map((s) => s.trim()).filter(Boolean),
                status: form.status,
            };
            if (editing) {
                await api.put(`/admin/parents/${editing.id}`, payload);
                toast.success("Parent updated");
            } else {
                payload.send_welcome = form.send_welcome;
                await api.post("/admin/parents", payload);
                toast.success(form.send_welcome ? "Parent created — welcome email queued" : "Parent created");
            }
            setOpenForm(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        } finally {
            setSubmitting(false);
        }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/admin/parents/${confirmDelete.id}`);
            toast.success("Parent deleted and access revoked");
            setConfirmDelete(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete");
        }
    };

    const resendOtp = async (p) => {
        try {
            await api.post(`/admin/parents/${p.id}/resend-otp`);
            toast.success(`OTP sent to ${p.email}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send OTP");
        }
    };

    const sendWelcome = async (p) => {
        try {
            await api.post(`/admin/parents/${p.id}/send-welcome`);
            toast.success(`Welcome email queued for ${p.email}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send welcome");
        }
    };

    const sendWelcomeAll = async () => {
        if (!window.confirm("Send a welcome email to ALL active parents?")) return;
        try {
            const r = await api.post("/admin/parents/send-welcome-all");
            toast.success(`Welcome email queued for ${r.data.queued} parent(s)`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send bulk welcome");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-parents-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Management</div>
                    <h1 className="font-heading text-3xl font-black tracking-tight mt-1">Parents</h1>
                    <p className="text-muted-foreground text-sm mt-1">Add, edit, search, and manage parent accounts.</p>
                </div>
                <Button onClick={openCreate} className="rounded-full" data-testid="add-parent-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add Parent
                </Button>
                <Button onClick={sendWelcomeAll} variant="outline" className="rounded-full" data-testid="send-welcome-all-btn">
                    <PartyPopper className="h-4 w-4 mr-2" /> Send Welcome to All
                </Button>
            </div>

            <Card className="rounded-2xl">
                <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by name, email or child"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10 h-11 rounded-full"
                                data-testid="parents-search-input"
                            />
                        </div>
                        <Input
                            placeholder="Filter by child name"
                            value={child}
                            onChange={(e) => setChild(e.target.value)}
                            className="h-11 rounded-full sm:w-64"
                            data-testid="parents-child-filter"
                        />
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                    ) : parents.length === 0 ? (
                        <div className="text-center py-16">
                            <Users className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">No parents found.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {parents.map((p) => (
                                <div key={p.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 px-5 py-4" data-testid={`parent-row-${p.id}`}>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-bold font-heading">{p.name}</div>
                                            {p.status ? (
                                                <Badge variant="secondary" className="rounded-full text-xs">Active</Badge>
                                            ) : (
                                                <Badge variant="destructive" className="rounded-full text-xs">Inactive</Badge>
                                            )}
                                        </div>
                                        <div className="text-sm text-muted-foreground truncate">{p.email} · {p.mobile || "no phone"}</div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                            Child: {(p.child_names || []).join(", ") || "—"}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => sendWelcome(p)} data-testid={`send-welcome-${p.id}`}>
                                            <PartyPopper className="h-3 w-3 mr-1" /> Welcome
                                        </Button>
                                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => resendOtp(p)} data-testid={`resend-otp-${p.id}`}>
                                            <Mail className="h-3 w-3 mr-1" /> Send OTP
                                        </Button>
                                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(p)} data-testid={`edit-parent-${p.id}`}>
                                            <Edit2 className="h-3 w-3 mr-1" /> Edit
                                        </Button>
                                        <Button size="sm" variant="destructive" className="rounded-full" onClick={() => setConfirmDelete(p)} data-testid={`delete-parent-${p.id}`}>
                                            <Trash2 className="h-3 w-3 mr-1" /> Delete
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create / Edit dialog */}
            <Dialog open={openForm} onOpenChange={setOpenForm}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{editing ? "Edit Parent" : "Add Parent"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={save} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Parent Name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11 rounded-xl" data-testid="parent-form-name" />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="h-11 rounded-xl" data-testid="parent-form-email" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Mobile</Label>
                                <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="h-11 rounded-xl" data-testid="parent-form-mobile" />
                            </div>
                            <div className="space-y-2">
                                <Label>Status</Label>
                                <div className="h-11 flex items-center gap-3">
                                    <Switch checked={form.status} onCheckedChange={(c) => setForm({ ...form, status: c })} data-testid="parent-form-status" />
                                    <span className="text-sm">{form.status ? "Active" : "Inactive"}</span>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Address</Label>
                            <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-xl" data-testid="parent-form-address" />
                        </div>
                        <div className="space-y-2">
                            <Label>Child Name(s) (comma separated)</Label>
                            <Input value={form.child_names} onChange={(e) => setForm({ ...form, child_names: e.target.value })} className="h-11 rounded-xl" placeholder="Aarav, Diya" data-testid="parent-form-children" />
                        </div>
                        {!editing && (
                            <div className="flex items-center justify-between bg-muted rounded-xl px-4 py-3">
                                <div>
                                    <div className="font-semibold text-sm">Send welcome email</div>
                                    <div className="text-xs text-muted-foreground">Introduce the portal and show sign-in instructions.</div>
                                </div>
                                <Switch checked={form.send_welcome} onCheckedChange={(c) => setForm({ ...form, send_welcome: c })} data-testid="parent-form-welcome-toggle" />
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpenForm(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" disabled={submitting} className="rounded-full" data-testid="parent-form-submit">
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save Changes" : "Create Parent")}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this parent?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-semibold">{confirmDelete?.name}</span> will lose access immediately. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} className="rounded-full bg-destructive text-destructive-foreground" data-testid="confirm-delete-parent">
                            Yes, delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
